import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const PAY_COLORS = {
  paid:    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  failed:  "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  comp:    "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
};

function fmtMoney(amount, currency) {
  const locale = (currency || "usd") === "usd" ? "en-US" : "es-MX";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency", currency: (currency || "USD").toUpperCase(),
    }).format(amount || 0);
  } catch { return `${amount} ${currency}`; }
}
function fmtDateTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }); }
  catch { return s; }
}

export default function AdminPayments() {
  const [status, setStatus] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-payments", status],
    queryFn: async () => (await api.get("/admin/payments", {
      params: { status: status === "all" ? undefined : status, limit: 500 },
    })).data,
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Pagos</h1>
        <p className="text-sm text-muted-foreground mt-1">{items.length} transacciones.</p>
      </div>

      <div className="flex">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-payments-status" className="w-56">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="paid">Pagados</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="failed">Fallidos</SelectItem>
            <SelectItem value="comp">Cortesía (comp)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Aún no hay transacciones.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/40 dark:border-white/10">
                  <tr>
                    <th className="px-4 py-3">Usuario</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Monto</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 hidden md:table-cell">Proveedor</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.txn_id} data-testid={`admin-payment-row-${p.txn_id}`}
                        className="border-b border-white/30 dark:border-white/5 last:border-0 hover:bg-white/30 dark:hover:bg-white/5 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium truncate">{p.user?.name || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.user?.email || p.user_id}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{p.plan_id || "—"}</td>
                      <td className="px-4 py-3 font-medium">{fmtMoney(p.amount, p.currency)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={PAY_COLORS[p.payment_status] || ""}>
                          {p.payment_status || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell capitalize text-muted-foreground">{p.provider || "—"}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{fmtDateTime(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
